import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmt } from "@/lib/format";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";
import { Wallet, TrendingUp, ShieldCheck } from "lucide-react";

const N_COLS = 8; // 4 actual + 4 forecast quarters
const LABEL_SPAN = 3;
const TOTAL_COLS = N_COLS + LABEL_SPAN;

function PeriodHeader({ periods, actualCount = 4 }) {
  return (
    <div
      className="grid items-center gap-1.5 border-b border-zinc-800 pb-2"
      style={{ gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(0, 1fr))` }}
    >
      <div
        className="text-[10px] uppercase tracking-widest text-zinc-500"
        style={{ gridColumn: `span ${LABEL_SPAN}` }}
      >
        季度
      </div>
      {periods.map((p, i) => (
        <div
          key={p}
          className={`text-[10px] uppercase tracking-widest text-right font-mono ${
            i >= actualCount ? "text-blue-400" : "text-zinc-500"
          }`}
          title={i >= actualCount ? "AI 推估：Finnhub YoY ±40% 上限外推，非分析師 consensus" : undefined}
        >
          {p}
          <div
            className={`text-[8px] mt-0.5 tracking-widest font-semibold ${
              i >= actualCount ? "text-blue-400/80" : "text-emerald-400/70"
            }`}
          >
            {i >= actualCount ? "AI 推估" : "實際"}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ label, series, unit = "", accent = false, digits = 1 }) {
  return (
    <div
      className="grid items-center gap-1.5 border-b border-zinc-800/50 py-2.5 last:border-b-0"
      style={{ gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(0, 1fr))` }}
    >
      <div
        className="text-xs sm:text-sm text-zinc-400"
        style={{ gridColumn: `span ${LABEL_SPAN}` }}
      >
        {label}
      </div>
      {series.map((q, i) => {
        const color = q.is_forecast
          ? "text-blue-300"
          : accent
          ? "text-emerald-400 font-semibold"
          : "text-zinc-100";
        return (
          <div
            key={i}
            className={`font-mono text-xs sm:text-sm text-right ${color} ${
              q.is_forecast ? "italic" : ""
            }`}
          >
            {q.value == null ? "—" : `${fmt.num(q.value, digits)}${unit}`}
          </div>
        );
      })}
    </div>
  );
}

function BeatMissRow({ label, series }) {
  return (
    <div
      className="grid items-center gap-1.5 border-b border-zinc-800/50 py-2 last:border-b-0"
      style={{ gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(0, 1fr))` }}
    >
      <div
        className="text-[10px] uppercase tracking-widest text-zinc-500"
        style={{ gridColumn: `span ${LABEL_SPAN}` }}
      >
        {label}
      </div>
      {series.map((q, i) => {
        if (q.is_forecast || q.prior_estimate == null) {
          return <div key={i} className="text-[10px] text-zinc-600 text-right font-mono">—</div>;
        }
        const surprise = ((q.value - q.prior_estimate) / q.prior_estimate) * 100;
        const beat = surprise >= 0;
        return (
          <div
            key={i}
            className={`text-[10px] text-right font-mono font-semibold ${
              beat ? "text-emerald-400" : "text-red-400"
            }`}
          >
            {beat ? "▲" : "▼"} {beat ? "+" : ""}
            {surprise.toFixed(1)}%
          </div>
        );
      })}
    </div>
  );
}

function ForecastRangeRow({ label, series }) {
  return (
    <div
      className="grid items-center gap-1.5 border-b border-zinc-800/50 py-2 last:border-b-0"
      style={{ gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(0, 1fr))` }}
    >
      <div
        className="text-[10px] uppercase tracking-widest text-zinc-500"
        style={{ gridColumn: `span ${LABEL_SPAN}` }}
      >
        {label}
      </div>
      {series.map((q, i) => {
        if (!q.is_forecast || q.est_low == null) {
          return <div key={i} className="text-[10px] text-zinc-600 text-right font-mono">—</div>;
        }
        return (
          <div key={i} className="text-[10px] text-blue-400/80 text-right font-mono">
            {fmt.num(q.est_low, 1)}–{fmt.num(q.est_high, 1)}
          </div>
        );
      })}
    </div>
  );
}

export default function FinancialMetrics({ data }) {
  const fin = data.financials;
  const periods = fin.period_labels || [];

  return (
    <section>
      <SectionTitle
        icon={Wallet}
        title="財務資料整理"
        subtitle="Quarterly · 過去 4 季實際 + 未來 4 季 AI 推估"
      />

      <div className="card-tactical p-4 sm:p-5 overflow-x-auto" data-testid={TID.financialTabs}>
        <Tabs defaultValue="profitability">
          <TabsList className="bg-zinc-900 border border-zinc-800 h-auto p-1">
            <TabsTrigger
              value="profitability"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm"
              data-testid={TID.financialTabTrigger}
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> 獲利能力
            </TabsTrigger>
            <TabsTrigger
              value="growth"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm"
              data-testid={TID.financialTabTrigger}
            >
              成長能力
            </TabsTrigger>
            <TabsTrigger
              value="safety"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-400 text-xs sm:text-sm"
              data-testid={TID.financialTabTrigger}
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> 財務安全性
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profitability" className="mt-5 min-w-[720px]">
            <PeriodHeader periods={periods} />
            <Row label="營收 (Bn)" series={fin.revenue} />
            <Row label="毛利 (Bn)" series={fin.gross_profit} />
            <Row label="毛利率" series={fin.gross_margin} unit="%" accent digits={1} />
            <Row label="營業利益 (Bn)" series={fin.operating_income} />
            <Row label="淨利 (Bn)" series={fin.net_income} />
            <Row label="EPS" series={fin.eps} digits={2} />
            <BeatMissRow label="EPS 對比 AI 推估" series={fin.eps} />
            <ForecastRangeRow label="營收推估區間 (Bn)" series={fin.revenue} />
            <p className="mt-4 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-zinc-800 pt-4">
              <span className="text-white font-semibold">AI 摘要：</span>
              {profitabilityCommentary(data)}
            </p>
          </TabsContent>

          <TabsContent value="growth" className="mt-5 min-w-[720px]">
            <PeriodHeader periods={periods} />
            <Row label="營收 (Bn)" series={fin.revenue} accent />
            <Row label="淨利 (Bn)" series={fin.net_income} accent />
            <Row label="EPS" series={fin.eps} accent digits={2} />
            <Row label="營運現金流 (Bn)" series={fin.operating_cashflow} />
            <BeatMissRow label="營收 vs AI 推估" series={fin.revenue} />
            <p className="mt-4 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-zinc-800 pt-4">
              <span className="text-white font-semibold">AI 摘要：</span>
              {growthCommentary(data)}
            </p>
          </TabsContent>

          <TabsContent value="safety" className="mt-5 min-w-[720px]">
            <PeriodHeader periods={periods} />
            <Row label="營運現金流 (Bn)" series={fin.operating_cashflow} />
            <Row label="自由現金流 (Bn)" series={fin.free_cashflow} />
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                資產負債表 · 年度
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <BalanceCard label="總資產 (Bn)" val={fin.total_assets?.slice(-1)[0]?.value} />
                <BalanceCard label="總負債 (Bn)" val={fin.total_liabilities?.slice(-1)[0]?.value} />
                <BalanceCard label="股東權益 (Bn)" val={fin.equity?.slice(-1)[0]?.value} />
              </div>
            </div>
            <p className="mt-4 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-zinc-800 pt-4">
              <span className="text-white font-semibold">AI 摘要：</span>
              {safetyCommentary(data)}
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

function BalanceCard({ label, val }) {
  return (
    <div className="card-tactical p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 metric-value text-lg font-bold text-white">
        {val == null ? "—" : fmt.num(val, 1)}
      </div>
    </div>
  );
}

// ------------ AI commentary helpers -------------------------------------

function ttmSum(series) {
  return (series || []).filter((q) => !q.is_forecast).reduce((a, b) => a + (b.value || 0), 0);
}

function forwardSum(series) {
  return (series || []).filter((q) => q.is_forecast).reduce((a, b) => a + (b.value || 0), 0);
}

function profitabilityCommentary(data) {
  const gm = data.financials.gross_margin || [];
  const lastActual = gm.filter((q) => !q.is_forecast).slice(-1)[0]?.value;
  const lastFwd = gm.filter((q) => q.is_forecast).slice(-1)[0]?.value;
  const delta = lastActual != null && lastFwd != null ? (lastFwd - lastActual).toFixed(1) : "—";
  return `${data.name_zh || data.name} 最新一季毛利率為 ${
    lastActual != null ? lastActual.toFixed(1) : "—"
  }%，AI 推估 4 季後為 ${
    lastFwd != null ? lastFwd.toFixed(1) : "—"
  }%（變動 ${delta > 0 ? "+" : ""}${delta} pp）。整體獲利能力${
    lastActual > 40 ? "強勁，屬於高毛利公司" : lastActual > 20 ? "穩健" : "偏低，需觀察成本結構"
  }。`;
}

function growthCommentary(data) {
  const revTTM = ttmSum(data.financials.revenue);
  const revFwd = forwardSum(data.financials.revenue);
  const yoy = revTTM > 0 ? ((revFwd - revTTM) / revTTM) * 100 : 0;
  return `過去 4 季 (TTM) 營收合計 ${revTTM.toFixed(
    1
  )}B，AI 推估未來 4 季合計 ${revFwd.toFixed(1)}B，年增率約 ${yoy.toFixed(1)}%，${
    yoy > 20
      ? "屬於高速成長"
      : yoy > 8
      ? "維持穩健成長"
      : yoy > 0
      ? "成長趨緩"
      : "面臨營收壓力"
  }。EPS 走勢亦需要觀察是否同步。`;
}

function safetyCommentary(data) {
  const eq = data.financials.equity || [];
  const debt = data.financials.total_liabilities || [];
  const fcf = (data.financials.free_cashflow || []).filter((q) => !q.is_forecast).slice(-1)[0];
  const lastDebt = debt.length ? debt[debt.length - 1]?.value : null;
  const lastEq = eq.length ? eq[eq.length - 1]?.value : null;
  const ratio =
    lastDebt != null && lastEq != null && lastDebt + lastEq > 0
      ? (lastDebt / (lastDebt + lastEq)) * 100
      : null;
  const ratioText = ratio == null ? "資料暫無" : `${ratio.toFixed(1)}%`;
  return `目前負債占總資本約 ${ratioText}。ROE ${fmt.num(data.roe, 1)}%、ROA ${fmt.num(
    data.roa,
    1
  )}%。最新一季自由現金流${
    fcf?.value != null
      ? fcf.value > 0
        ? "為正，公司具備自我造血能力"
        : "為負，需觀察資本支出節奏"
      : "資料暫無"
  }。`;
}
